import type { Bar } from '../core/types'

export type Side = 'buy' | 'sell'
export type Direction = 'long' | 'short'
export type ExitReason = 'tp' | 'sl' | 'manual' | 'reverse'

/** Per-instrument contract spec. The ONE place price-move → money is defined. */
export interface InstrumentSpec {
  symbol: string
  displayName: string
  /** account-currency value of a 1.0 price move, per 1.0 lot */
  valuePerPointPerLot: number
  priceStep: number
  lotStep: number
  minLot: number
  /** full spread in price units (bid/ask = mid -/+ spread/2) */
  spreadPoints: number
  commissionPerLotPerSide: number
  slippagePoints: number
  quoteCurrency: string
}

export interface Account {
  currency: string
  startingBalance: number
  /** realized balance (updated as trades close) */
  balance: number
}

export interface Position {
  id: string
  symbol: string
  direction: Direction
  size: number // lots, > 0
  avgEntryPrice: number
  entryTime: number
  tp: number | null
  sl: number | null
  /** SL captured at open, for honest R even if SL is later moved */
  initialSl: number | null
  /** account-ccy risk at open = |entry - initialSl| * vpp * size */
  initialRisk: number | null
  /** entry-side commission accrued for the currently open size */
  entryCommission: number
}

export interface Trade {
  id: string
  symbol: string
  direction: Direction
  size: number
  entryTime: number
  entryPrice: number
  exitTime: number
  exitPrice: number
  tp: number | null
  sl: number | null
  initialSl: number | null
  grossPnL: number
  commission: number
  netPnL: number
  rMultiple: number | null
  exitReason: ExitReason
}

export type SimEvent =
  | { kind: 'fill'; symbol: string; side: Side; price: number; time: number; size: number }
  | { kind: 'close'; trade: Trade }
  | { kind: 'reject'; reason: string }

export interface SimConfig {
  /** FT-style instant fill at the current bar close (default), or next bar open. */
  marketFill: 'currentClose' | 'nextOpen'
  /** when one bar's range contains both TP and SL */
  ambiguousBar: 'pessimistic' | 'optimistic'
}

export const DEFAULT_SIM_CONFIG: SimConfig = {
  marketFill: 'currentClose',
  ambiguousBar: 'pessimistic',
}

export interface AccountState {
  balance: number
  unrealized: number
  equity: number
}

export type { Bar }
